import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from '../components/AuthShell';
import Button from '../components/Button';
import FormField from '../components/FormField';
import Toast from '../components/Toast';
import { authApi } from '../api';
import {
  OIDC_START_PATH,
  QUERY_PARAM_NEXT,
  QUERY_PARAM_REASON,
  REASON_LABEL,
  REASON_REF,
  RESTARTABLE_REASONS,
  ROUTE_MAGIC,
} from '../constants';
import { recordAutoRestart, shouldAutoRestart } from '../lib/restartGuard';
import { shouldAutoStartSso } from '../lib/ssoAutoStart';
import { useAuthCaps } from '../lib/useAuthCaps';
import { useNextParam } from '../lib/useNextParam';

type Mode = 'password' | 'magic';

const PLACEHOLDER_EMAIL = 'you@example.com';
const PLACEHOLDER_PASSWORD = '••••••••';
const COPY_SIGN_IN = 'Sign in';
const COPY_SIGN_IN_SUBTITLE = 'Use your single sign-on account to continue.';
const COPY_EMAIL_LABEL = 'Email';
const COPY_PASSWORD_LABEL = 'Password';
const COPY_USE_MAGIC = 'Email me a sign-in link instead';
const COPY_USE_PASSWORD = 'Use a password instead';
const COPY_OIDC = 'Continue with single sign-on';
const COPY_SUBMIT_PASSWORD = 'Sign in';
const COPY_SUBMIT_MAGIC = 'Send sign-in link';
const COPY_OR = 'or';
const COPY_SSO_REDIRECTING_TITLE = 'Taking you to sign-in';
const COPY_SSO_REDIRECTING_BODY = 'Redirecting to your single sign-on provider…';

// One-shot transparent recovery: a restartable failure (stale state
// from a parallel window, a consumed code) restarts the flow without
// asking. See lib/restartGuard.ts for the loop guard.
const COPY_RESTARTING_TITLE = 'Restarting your sign-in';
const COPY_RESTARTING_BODY = 'That attempt was stale — starting a fresh one…';

// Second consecutive failure: stop being clever, hand it to the human.
const COPY_EXPIRED_TITLE = 'This sign-in link expired';
const COPY_EXPIRED_BODY = 'Start again.';
const COPY_RESTART_BUTTON = 'Start sign-in again';

// Copy shown when the deployment has no sign-in method enabled at all
// (no SSO, password, or magic link). This is a misconfiguration rather
// than a normal state, so we render an explanation instead of a
// button-less form.
const COPY_NO_METHOD_TITLE = 'Sign-in isn’t available';
const COPY_NO_METHOD_BODY =
  'No sign-in method is configured for this Fred Cloud deployment yet. Please contact your administrator.';

export default function LoginScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = useNextParam();
  const caps = useAuthCaps();
  const reason = searchParams.get(QUERY_PARAM_REASON) ?? '';
  const reasonMessage = REASON_LABEL[reason] ?? null;
  const diagnosticRef = REASON_REF[reason] ?? null;

  const passwordEnabled = caps.password;
  const magicEnabled = caps.magic_link;
  const oidcEnabled = caps.oidc;
  // The email + submit form only drives password / magic-link. OIDC is a
  // separate button. Don't render the form (and its submit) unless one of
  // those form methods is actually enabled — otherwise the form would
  // present "Send sign-in link" and POST to a disabled endpoint.
  const formMethodEnabled = passwordEnabled || magicEnabled;
  const anyMethodEnabled = formMethodEnabled || oidcEnabled;
  // Pick the initial mode based on what's actually enabled. If only
  // magic-link is on, default the form to magic-link instead of
  // password (which would be hidden anyway).
  const initialMode: Mode = passwordEnabled ? 'password' : 'magic';

  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [oidcLoading, setOidcLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Decided ONCE at mount (useState initializer) so recording the
  // attempt below can never flip the interstitial mid-navigation.
  const [autoRestartSso] = useState(() => oidcEnabled && shouldAutoRestart(reason));
  const restartBlocked = oidcEnabled && RESTARTABLE_REASONS.has(reason) && !autoRestartSso;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'password') {
        const result = await authApi.password(email, password, next);
        if ('ok' in result && result.ok) {
          window.location.assign(result.next);
          return;
        }
        setError(result.message);
        return;
      }
      const result = await authApi.magic(email, next);
      if ('ok' in result && result.ok) {
        navigate(`${ROUTE_MAGIC}?email=${encodeURIComponent(email)}`);
        return;
      }
      setError(result.message);
    } finally {
      setSubmitting(false);
    }
  }

  function onOidcClick() {
    // Single sign-on is a server-driven BFF flow: navigate the whole
    // tab to the cloud's /v1/auth/oidc/start, which mints the PKCE
    // verifier server-side and 302s on to the external IdP — or, when
    // this session turns out to already be signed in (the other window
    // finished first), 302s straight to the dashboard. Every start
    // mints FRESH state (last writer wins), so a retry is never wedged
    // by a stale tab. No fetch, no token handling in the SPA.
    setError(null);
    setOidcLoading(true);
    const target = next
      ? `${OIDC_START_PATH}?${QUERY_PARAM_NEXT}=${encodeURIComponent(next)}`
      : OIDC_START_PATH;
    window.location.assign(target);
  }

  // Single-method auto-redirect: when SSO is the only interactive
  // sign-in method AND the visit carries no `reason` param, skip the
  // interstitial entirely and start the BFF flow immediately. Visits
  // WITH a reason stay on this screen so the banner is visible and
  // stable — EXCEPT the restartable failure class, which gets exactly
  // one transparent auto-restart (fresh state, preserved `next`),
  // loop-guarded in sessionStorage. See lib/ssoAutoStart.ts and
  // lib/restartGuard.ts.
  const autoStartSso = shouldAutoStartSso(caps, reason);
  useEffect(() => {
    if (autoStartSso) {
      onOidcClick();
      return;
    }
    if (autoRestartSso) {
      recordAutoRestart();
      onOidcClick();
    }
    // Mount-only by design: the caps global and the reason param are
    // fixed for the lifetime of this document; re-running on state
    // churn could double-fire the navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (autoStartSso || autoRestartSso) {
    return (
      <AuthShell
        title={autoRestartSso ? COPY_RESTARTING_TITLE : COPY_SSO_REDIRECTING_TITLE}
        subtitle={autoRestartSso ? COPY_RESTARTING_BODY : COPY_SSO_REDIRECTING_BODY}
      >
        <Button type="button" loading className="w-full">
          {COPY_OIDC}
        </Button>
      </AuthShell>
    );
  }

  // The transparent restart already failed once (or storage is
  // unavailable): human copy, one clear way forward, diagnostic ref.
  if (restartBlocked) {
    return (
      <AuthShell
        title={COPY_EXPIRED_TITLE}
        subtitle={COPY_EXPIRED_BODY}
        diagnosticRef={diagnosticRef}
      >
        <div className="space-y-4">
          {reasonMessage ? <Toast variant="info">{reasonMessage}</Toast> : null}
          <Button type="button" loading={oidcLoading} className="w-full" onClick={onOidcClick}>
            {COPY_RESTART_BUTTON}
          </Button>
        </div>
      </AuthShell>
    );
  }

  // No method enabled: don't render the form at all. Render the
  // explanation card and keep the reason banner so the user still sees
  // *why* they bounced here (e.g. expired token).
  if (!anyMethodEnabled) {
    return (
      <AuthShell
        title={COPY_NO_METHOD_TITLE}
        subtitle={COPY_NO_METHOD_BODY}
        diagnosticRef={diagnosticRef}
      >
        {reasonMessage ? <Toast variant="info">{reasonMessage}</Toast> : null}
      </AuthShell>
    );
  }

  // Toggle between password and magic only makes sense if both are
  // wired. With only one direct method enabled we hide the toggle.
  const showModeToggle = passwordEnabled && magicEnabled;
  const toggleLabel = mode === 'password' ? COPY_USE_MAGIC : COPY_USE_PASSWORD;
  const submitLabel = mode === 'password' ? COPY_SUBMIT_PASSWORD : COPY_SUBMIT_MAGIC;

  return (
    <AuthShell
      title={COPY_SIGN_IN}
      subtitle={oidcEnabled && !formMethodEnabled ? COPY_SIGN_IN_SUBTITLE : undefined}
      diagnosticRef={diagnosticRef}
    >
      {reasonMessage ? (
        <div className="mb-4">
          <Toast variant="info">{reasonMessage}</Toast>
        </div>
      ) : null}
      {error ? (
        <div className="mb-4">
          <Toast variant="error">{error}</Toast>
        </div>
      ) : null}
      {formMethodEnabled ? (
        <form onSubmit={onSubmit} className="space-y-4">
          <FormField
            id="auth-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            label={COPY_EMAIL_LABEL}
            placeholder={PLACEHOLDER_EMAIL}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {mode === 'password' && passwordEnabled ? (
            <FormField
              id="auth-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              label={COPY_PASSWORD_LABEL}
              placeholder={PLACEHOLDER_PASSWORD}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          ) : null}
          <Button type="submit" loading={submitting} className="w-full">
            {submitLabel}
          </Button>
          {showModeToggle ? (
            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setMode((current) => (current === 'password' ? 'magic' : 'password'))}
            >
              {toggleLabel}
            </Button>
          ) : null}
        </form>
      ) : null}
      {formMethodEnabled && oidcEnabled ? (
        <div className="my-4 flex items-center gap-3 text-xs uppercase tracking-wide text-dark-muted">
          <span className="h-px flex-1 bg-white/10" />
          {COPY_OR}
          <span className="h-px flex-1 bg-white/10" />
        </div>
      ) : null}
      {oidcEnabled ? (
        <Button
          type="button"
          variant={formMethodEnabled ? 'secondary' : 'primary'}
          loading={oidcLoading}
          className="w-full"
          onClick={onOidcClick}
        >
          {COPY_OIDC}
        </Button>
      ) : null}
    </AuthShell>
  );
}
